import React, { useState } from 'react';
import {
  InputGroup,
  InputRightElement,
  Input,
  IconButton,
  Tooltip,
  Box,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { SearchIcon, CloseIcon, InfoIcon } from '@chakra-ui/icons';
import { SEARCHBOX_STYLES } from './SearchBox.styles';
import { SEARCHABLE_FIELDS } from '../constants/constants';

const SearchBox = ({ onSearch, onClear, type }) => {  // Add props for search and clear handlers
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // Get searchable fields for the current type
  const searchableFields = type ? SEARCHABLE_FIELDS[type.toLowerCase()] || [] : [];

  const handleSearch = () => {
    if (searchText.trim()) {
      setIsSearching(true);
      onSearch(searchText.trim());  // Call parent's search handler
    }
  };

  const handleClear = () => {
    setSearchText('');
    setIsSearching(false);
    onClear();  // Call parent's clear handler to reset results
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchText.trim()) {
      handleSearch();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    
    // If text is cleared manually, trigger clear function
    if (!newValue) {
      setIsSearching(false);
      onClear();
    }
  };

  // Combine base styles with specific button styles
  const searchButtonStyles = {
    ...SEARCHBOX_STYLES.iconButton.base,
    ...SEARCHBOX_STYLES.iconButton.search
  };
  
  const clearButtonStyles = {
    ...SEARCHBOX_STYLES.iconButton.base,
    ...SEARCHBOX_STYLES.iconButton.clear
  };

  // Generate placeholder text based on type
  const getPlaceholder = () => {
    if (searchableFields.length > 0) {
      return `Search by ${searchableFields.slice(0, 2).join(', ')}${searchableFields.length > 2 ? '...' : ''}`;
    }
    return "Search records...";
  };

  // Generate tooltip content
  const getTooltipContent = () => {
    if (searchableFields.length === 0) {
      return "Search records";
    }
    return (
      <Box p={2}>
        <Text fontWeight="bold" mb={2} fontSize="sm">
          Searchable Fields:
        </Text>
        <VStack align="start" spacing={1}>
          {searchableFields.map((field, index) => (
            <HStack key={index} spacing={2}>
              <Text fontSize="xs" color="gray.300">•</Text>
              <Text fontSize="xs" color="white">{field}</Text>
            </HStack>
          ))}
        </VStack>
      </Box>
    );
  };

  return (
    <HStack spacing={2} alignItems="center">
    <InputGroup {...SEARCHBOX_STYLES.container}>
      {!isSearching && (
        <InputRightElement {...SEARCHBOX_STYLES.rightElement}>
          <IconButton
            icon={<SearchIcon {...SEARCHBOX_STYLES.icons.search} />}
            aria-label="Search"
            onClick={handleSearch}
            isDisabled={!searchText.trim()}
            {...searchButtonStyles}
          />
        </InputRightElement>
      )}
      <Input
        value={searchText}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
        placeholder={getPlaceholder()}
        {...SEARCHBOX_STYLES.input}
      />
      {isSearching && searchText && (
        <InputRightElement {...SEARCHBOX_STYLES.rightElement}>
          <IconButton
            icon={<CloseIcon {...SEARCHBOX_STYLES.icons.close} />}
            aria-label="Clear search"
            onClick={handleClear}
            {...clearButtonStyles}
          />
        </InputRightElement>
      )}
    </InputGroup>
      {searchableFields.length > 0 && (
        <Tooltip
          label={getTooltipContent()}
          placement="top"
          hasArrow
          bg="gray.800"
          color="white"
          fontSize="xs"
          px={3}
          py={2}
          borderRadius="md"
          maxW="250px"
        >
          <IconButton
            icon={<InfoIcon />}
            aria-label="Search information"
            size="sm"
            variant="ghost"
            colorScheme="blue"
            color="blue.500"
            _hover={{
              bg: "blue.50",
              color: "blue.600",
              transform: "scale(1.1)"
            }}
            transition="all 0.2s ease"
          />
        </Tooltip>
      )}
    </HStack>
  );
};

export default SearchBox;